import { Header, Sidebar } from '@/components/layout';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { useDebounce } from '@/hooks/useDebounce';
import { useDashboardSessions } from '@/lib/api';
import { formatDelegatorLabel } from '@/types/delegate';
import { useQueryClient } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, Clock, Loader2, MessageSquare, Mic, Plus, Search, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'wouter';

interface AudioSession {
  id: string;
  patient_id?: string;
  topic?: string;
  language?: string;
  duration?: number;
  created_at?: string;
  diarized?: boolean;
}

const PAGE_SIZE = 20;

export default function Dashboard() {
  const { user, viewingAsAccount, isViewingAsDelegate } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebounce(searchQuery, 300);
  const [page, setPage] = useState(1);
  const [sessionToDelete, setSessionToDelete] = useState<AudioSession | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Reset to page 1 when search changes
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  // Greeting name: when viewing as a delegator (auth has no display name) use a
  // short account-id label; otherwise the user's own first name.
  const displayName =
    isViewingAsDelegate && viewingAsAccount
      ? formatDelegatorLabel(viewingAsAccount.delegator_account_id)
      : user?.name?.split(' ')[0] || 'Notandi';
  const firstName = displayName;

  // Fetch paginated dashboard sessions
  const { data: dashboardData, isLoading: sessionsLoading } = useDashboardSessions(
    page,
    PAGE_SIZE,
    debouncedSearch || undefined
  );

  const sessions = dashboardData?.sessions;
  const stats = dashboardData?.stats;
  const pagination = dashboardData?.pagination;
  const totalPages = pagination ? Math.ceil(pagination.total / pagination.page_size) : 0;

  const handleDeleteSession = async () => {
    if (!sessionToDelete) return;

    setIsDeleting(true);
    try {
      const response = await fetch(`/api/sessions/${sessionToDelete.id}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      const data = await response.json();

      if (response.ok) {
        if (data.warning) {
          toast({
            title: 'Lotu eytt með viðvörun',
            description: data.warning,
            variant: 'destructive',
          });
        } else {
          toast({
            title: 'Lotu eytt',
            description: 'Lotan og öll tengd gögn hafa verið fjarlægð.',
          });
        }
        setSessionToDelete(null);
        queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      } else {
        toast({
          title: 'Villa',
          description: data.error || 'Ekki tókst að eyða lotu.',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Error deleting session:', error);
      toast({
        title: 'Villa',
        description: 'Óvænt villa kom upp við að eyða lotu.',
        variant: 'destructive',
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const getPatientName = (session: { patient_name?: string }) => {
    return session.patient_name || 'Ónafngreindur sjúklingur';
  };

  // Helper to format date/time
  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    const today = new Date();
    const isToday = date.toDateString() === today.toDateString();

    const time = date.toLocaleTimeString('is-IS', { hour: '2-digit', minute: '2-digit' });

    if (isToday) {
      return `Í dag ${time}`;
    }
    const dateStr = date.toLocaleDateString('is-IS', { day: '2-digit', month: '2-digit' });
    return `${dateStr} ${time}`;
  };

  // Helper to get session status
  const getSessionStatus = (session: { summarized?: boolean; transcribed?: boolean; diarized?: boolean }) => {
    if (session.summarized) return { label: 'Lokið', variant: 'secondary' as const };
    if (session.transcribed) return { label: 'Unnið', variant: 'default' as const };
    if (session.diarized) return { label: 'Í gangi', variant: 'default' as const };
    return { label: 'Bíður', variant: 'secondary' as const };
  };

  // Helper to check if session is a conversation (either by ID prefix or diarized flag)
  const isConversationSession = (session: { id?: string; diarized?: boolean }) => {
    return session.id?.startsWith('conv_') || session.diarized === true;
  };

  // Helper to get the appropriate link for a session
  const getSessionLink = (session: { id?: string; diarized?: boolean }) => {
    if (isConversationSession(session)) {
      return `/conversation/${session.id}`;
    }
    return `/dictation/${session.id}`;
  };

  return (
    <div className="flex h-screen bg-background">
      <Sidebar />
      <div className="flex flex-1 flex-col overflow-hidden">
        <Header title="Mælaborð" />

        <main className="flex-1 overflow-auto p-6">
          <div className="mx-auto max-w-5xl space-y-8">
            {/* Hero Section */}
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-bold font-display text-3xl text-slate-900">Góðan daginn, {firstName}</h2>
                <p className="mt-1 text-slate-500">
                  {stats
                    ? `Þú átt ${stats.total} ${stats.total === 1 ? 'lotu' : 'lotur'} skráðar.`
                    : 'Engar lotur skráðar.'}
                </p>
              </div>
              <Link href="/session/new">
                <Button
                  size="lg"
                  className="h-12 px-6 shadow-lg shadow-primary/20 transition-all hover:shadow-primary/30"
                >
                  <Plus className="mr-2 h-5 w-5" />
                  Hefja nýja lotu
                </Button>
              </Link>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
              <Card>
                <CardContent className="p-6">
                  <p className="font-medium text-muted-foreground text-sm">Lotur í dag</p>
                  <div className="mt-2 flex items-baseline gap-2">
                    <span className="font-bold text-3xl text-slate-900">{stats?.today ?? 0}</span>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <p className="font-medium text-muted-foreground text-sm">Lotum lokið</p>
                  <div className="mt-2 flex items-baseline gap-2">
                    <span className="font-bold text-3xl text-slate-900">{stats?.summarized ?? 0}</span>
                    <span className="text-muted-foreground text-xs">af {stats?.total ?? 0}</span>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <div className="mb-2 flex items-center gap-2 text-muted-foreground">
                    <Mic className="h-4 w-4" />
                    <p className="font-medium text-sm">Dikteringar</p>
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span className="font-bold text-3xl text-slate-900">{stats?.dictations ?? 0}</span>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <div className="mb-2 flex items-center gap-2 text-muted-foreground">
                    <MessageSquare className="h-4 w-4" />
                    <p className="font-medium text-sm">Viðtöl</p>
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span className="font-bold text-3xl text-slate-900">{stats?.conversations ?? 0}</span>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Recent Sessions */}
            <Card>
              <CardHeader className="border-border border-b pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle>Nýlegar lotur</CardTitle>
                  <div className="relative w-64">
                    <Search className="absolute top-2.5 left-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Leita að sjúklingum..."
                      className="h-9 border-slate-200 bg-slate-50 pl-9"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {sessionsLoading ? (
                  <div className="flex items-center justify-center p-12">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : !sessions || sessions.length === 0 ? (
                  <div className="p-12 text-center text-muted-foreground">
                    <p>{debouncedSearch ? 'Engar lotur fundust' : 'Engar lotur skráðar'}</p>
                  </div>
                ) : (
                  <div className="divide-y divide-border">
                    {sessions.map((session) => {
                      const status = getSessionStatus(session);
                      const patientName = getPatientName(session);
                      const dateTime = session.created_at ? formatDateTime(session.created_at) : '';
                      const isConversation = isConversationSession(session);
                      const sessionLink = getSessionLink(session);

                      return (
                        <Link key={session.id} href={sessionLink}>
                          <div className="group flex cursor-pointer items-center justify-between p-4 transition-colors hover:bg-slate-50">
                            <div className="flex items-center gap-4">
                              <div
                                className={`flex h-10 w-10 items-center justify-center rounded-full ${
                                  isConversation ? 'bg-blue-100 text-blue-600' : 'bg-slate-100 text-slate-500'
                                }`}
                              >
                                {isConversation ? <MessageSquare className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <p className="font-medium text-slate-900">{patientName}</p>
                                  {isConversation && (
                                    <Badge
                                      variant="outline"
                                      className="border-blue-200 font-normal text-blue-600 text-xs"
                                    >
                                      Viðtal
                                    </Badge>
                                  )}
                                </div>
                                <div className="flex items-center gap-2 text-muted-foreground text-xs">
                                  <Clock className="h-3 w-3" />
                                  {dateTime}
                                  {session.topic && (
                                    <>
                                      <span>•</span>
                                      <span>{session.topic}</span>
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>

                            <div className="flex items-center gap-4">
                              <Badge variant={status.variant} className="font-normal">
                                {status.label}
                              </Badge>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-muted-foreground opacity-0 transition-opacity hover:text-destructive group-hover:opacity-100"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setSessionToDelete(session as AudioSession);
                                }}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                              <ChevronRight className="h-4 w-4 text-muted-foreground/30" />
                            </div>
                          </div>
                        </Link>
                      );
                    })}

                    {/* Pagination */}
                    {totalPages > 1 && (
                      <div className="flex items-center justify-between border-border border-t px-4 py-3">
                        <p className="text-muted-foreground text-sm">
                          Síða {page} af {totalPages} ({pagination?.total} lotur)
                        </p>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={page <= 1}
                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                          >
                            <ChevronLeft className="mr-1 h-4 w-4" />
                            Fyrri
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={page >= totalPages}
                            onClick={() => setPage((p) => p + 1)}
                          >
                            Næsta
                            <ChevronRight className="ml-1 h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </main>
      </div>

      {/* Delete Session Confirmation */}
      <AlertDialog open={!!sessionToDelete} onOpenChange={(open) => !open && setSessionToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eyða lotu?</AlertDialogTitle>
            <AlertDialogDescription>
              Þessi aðgerð er óafturkræf. Lotan og öll tengd gögn (upptaka, umritun, samantekt) verða varanlega
              fjarlægð.
              {sessionToDelete?.created_at && (
                <span className="mt-2 block font-medium">
                  Lota frá {new Date(sessionToDelete.created_at).toLocaleDateString('is-IS')}
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Hætta við</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteSession}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Eyðir...
                </>
              ) : (
                'Eyða lotu'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
